import React from 'react';
import { Link } from 'gatsby';
import { navigate, StaticQuery, graphql } from 'gatsby';
import classNames from 'classnames';
import qs from 'qs';
import { FiX } from 'react-icons/fi';
import Helmet from 'react-helmet';
import DefaultLayout from '.';
import * as styles from './templates.module.styl';
import templatesByGrouping from '../selectors/templatesByGrouping';
import truncate from '../utils/truncate';
import { ensureTrailingSlash } from '../utils/ensureTrailingSlash';
import formScreenshot from '../utils/formScreenshot';
import HeaderLeft from '../components/headers/HeaderLeft';

const query = graphql`
  query TemplatesQuery {
    paperformHelpTemplates {
      title
      meta {
        meta_description
        meta_image
        meta_title
      }
      image
      description
    }

    allPaperformHelpApp(filter: { public: { eq: true } }) {
      edges {
        node {
          title
          slug
          excerpt
          cover_image
        }
      }
    }

    allPaperformTemplate {
      edges {
        node {
          id
          title
          description
          form_cover_image_url
          form_slug
          key
          tags
          groups {
            id
            slug
            type
            title
          }
        }
      }
    }
  }
`;

export function Template({
  node,
  textColor = 'var(--text-darK)',
  showDescriptions = true,
  asSlider = false,
}) {
  const link = ensureTrailingSlash(node.link || `/templates/${node.key}/`);

  return (
    <Link
      to={link}
      key={node.form_slug}
      className={classNames(
        styles.templateWrapper,
        textColor === 'var(--text-light)' && styles.templateWrapperDark,
        asSlider && styles.templateWrapperSlider,
      )}
    >
      <div
        className={styles.template}
        style={{
          backgroundImage: `url(${
            node.form_slug
              ? formScreenshot(node.form_slug, {
                  width: 600,
                  screenWidth: 961,
                  fullPage: 1,
                })
              : `https://img.paperform.co/fetch/w_600,f_auto/${node.form_cover_image_url}`
          })`,
        }}
      />
      <div className={styles.templateContent} style={{ color: textColor }}>
        <div className={styles.templateTitle}>{node.title}</div>
        {showDescriptions && (
          <div className={styles.templateDescription}>
            {truncate(node.description, 50)}
          </div>
        )}
      </div>
    </Link>
  );
}

export function TemplateGrid({ children }) {
  return (
    <div
      key="res"
      style={{ display: 'flex', flexWrap: 'wrap' }}
      className={styles.templateGrid}
    >
      {children}
    </div>
  );
}

class Templates extends React.Component {
  componentDidMount() {
    const query = this.getSearchQuery();
    if (query && global.gtag) {
      setTimeout(() => {
        const resultsCount = this.getSearchResults(query).length;
        global.gtag('event', 'search', {
          event_category: 'Templates',
          event_label: query,
          value: resultsCount,
        });

        if (!resultsCount) {
          global.gtag('event', 'search-miss', {
            event_category: 'Templates',
            event_label: query,
          });
        }
      });
    }
  }

  getFeatured() {
    return this.props.data.allPaperformTemplate.edges.slice(0, 6);
  }

  getApps() {
    return this.props.data.allPaperformHelpApp.edges
      .map(({ node }) => node)
      .slice(0, 3);
  }

  getFormsByGrouping(results, type = 'category') {
    const g = templatesByGrouping(
      (results || this.props.data).allPaperformTemplate.edges,
      type,
    );

    return g;
  }

  getSearchQuery() {
    const res = qs.parse(
      (global.location &&
        global.location.search &&
        global.location.search.replace('?', '')) ||
        '',
    );
    return res && res.q;
  }

  getSearchResults(query) {
    const searchLower = (query && query.toLowerCase().replace(/ /g, '')) || '';
    return this.props.data.allPaperformTemplate.edges.filter(({ node }) => {
      const searchableArgs =
        node.title +
        node.description +
        (node.tags || '') +
        node.groups.map((g) => g.title).join('');

      return (
        searchableArgs.toLowerCase().replace(/ /g, '').indexOf(searchLower) !==
        -1
      );
    });
  }

  getContent(query, groupedForms) {
    if (query) return this.renderSearchResults(query);
    if (this.props.children) return this.props.children;

    if (this.props.results) {
      return this.renderSearchResults(null, this.props.results);
    }

    return (
      <div className={styles.templates}>
        {!this.props.results ? (
          <>
            <span key="featured" style={{ display: 'none' }}>
              <h2 className={styles.templateGroupFeaturedTitle}>
                Featured Templates{' '}
                <Link to="/templates/category/all/">View more...</Link>
              </h2>
              <div className={styles.templateGridFeatured}>
                <div className={styles.templateGridFeaturedInner}>
                  {this.getFeatured().map(({ node }) => (
                    <Link
                      className={styles.featuredTemplate}
                      to={`/templates/${node.key}/`}
                      style={{
                        backgroundImage: `url(https://img.paperform.co/fetch/w_800,f_auto/${node.form_cover_image_url})`,
                      }}
                    >
                      <div>{node.title}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </span>

            <div
              key="apps"
              style={{
                background: 'var(--dusk)',
                padding: '.5rem 2rem 0 2rem',
                borderRadius: '16px',
                marginBottom: '2rem',
              }}
            >
              <h2 className="text-2xl font-normal my-3">
                App Tutorials{' '}
                <Link
                  to="/templates/apps/"
                  className="text-[10px] hover:text-black relative -top-[1px] uppercase font-worksans text-salmon font-bold"
                >
                  View more...
                </Link>
                <br />
                <small
                  style={{ marginTop: '.4rem', opacity: 0.8 }}
                  className="font-worksans text-sm leading-[1]"
                >
                  Step-by-step guides on how to build custom apps for your
                  business.
                </small>
              </h2>

              <div className={styles.templateGridFeatured}>
                <div className={styles.templateGridFeaturedInner}>
                  {this.getApps().map((node) => (
                    <Link
                      to={`/templates/apps/${node.slug}/`}
                      key={node.slug}
                      className={styles.templateWrapper}
                    >
                      <div
                        className={styles.template}
                        style={{
                          backgroundImage: `url(https://img.paperform.co/fetch/w_600,f_auto/${node.cover_image})`,
                        }}
                      />
                      <div className={styles.templateContent}>
                        <div className={styles.templateTitle}>{node.title}</div>
                        <div className={styles.templateDescription}>
                          {truncate(node.excerpt, 50)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
        {groupedForms.map((group) => (
          <div key={group.slug}>
            <h3 className="text-2xl mb-1 mt-3 font-normal font-sonoma">
              {group.title}{' '}
              {group.templates.length > 3 ? (
                <Link
                  className="text-[10px] hover:text-black relative -top-[1px] uppercase font-worksans text-salmon font-bold"
                  to={`/templates/category/${group.slug}/`}
                >
                  View more...
                </Link>
              ) : null}
            </h3>
            <div className={styles.templateGrid}>
              {group.templates
                .reverse()
                .slice(0, 3)
                .map((node) => (
                  <Link
                    to={`/templates/${node.key}/`}
                    key={node.form_slug}
                    className={styles.templateWrapper}
                  >
                    <div
                      className={styles.template}
                      style={{
                        backgroundImage: `url(${formScreenshot(node.form_slug, {
                          width: 600,
                          clientWidth: 961,
                          fullPage: 1,
                        })})`,
                      }}
                    />
                    <div className={styles.templateContent}>
                      <div className={styles.templateTitle}>{node.title}</div>
                      <div className={styles.templateDescription}>
                        {truncate(node.description, 50)}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  searchForm(ref, query) {
    if (this.props.noSearch && ref === 'mobile') return null;
    return (
      <form
        style={{ margin: 0 }}
        onSubmit={(e) => {
          e.preventDefault();

          navigate(`/templates?q=${encodeURIComponent(this.refs[ref].value)}`);
        }}
      >
        <input
          ref={ref}
          defaultValue={query}
          className={styles.search}
          placeholder="Search..."
        />
        <button type="button" style={{ display: 'none' }} />
      </form>
    );
  }

  renderSearchResults(query, res) {
    const results = res || this.getSearchResults(query);

    return (
      <div key="search_results" className={styles.templates}>
        {res ? null : (
          <h2 className={styles.templateGroupFeaturedTitle}>
            {results.length} Templates Found{' '}
            <Link
              to="/templates/"
              style={{
                fontSize: '1.5rem',
                position: 'relative',
                top: '.25rem',
              }}
              onClick={() => {
                if (this.refs.mobile) this.refs.mobile.value = '';
                if (this.refs.desktop) this.refs.desktop.value = '';
              }}
            >
              <FiX />
            </Link>
          </h2>
        )}
        {!results.length ? (
          <p>Sorry, we couldn't find any templates for "{query}"</p>
        ) : null}
        <TemplateGrid>
          {results.map(({ node }) => (
            <Template node={node} />
          ))}
        </TemplateGrid>
      </div>
    );
  }

  render() {
    const groupedForms = this.getFormsByGrouping();

    const query = this.getSearchQuery();

    const {
      paperformHelpTemplates: { meta, title, image, description },
    } = this.props.data;

    return (
      <DefaultLayout
        wrapperClass={this.props.wrapperClass || 'bg-dusk-light'}
        noMeta={!!this.props.meta}
        location={this.props.location}
      >
        <Helmet
          title={
            this.props.metaTitle ||
            this.props.title ||
            meta?.meta_title ||
            'Templates'
          }
          description={
            meta?.meta_description ||
            'Create beautiful web forms fast with one of our designer form templates.'
          }
        />
        {this.props.meta}
        {this.props.noTitle ? (
          <div className="globalHeaderOffset" />
        ) : this.props.title ? (
          <HeaderLeft
            title={this.props.title}
            wrapperClass={this.props.headerWrapperClass}
            description={this.props.headerContent}
            image={this.props.headerImage}
            imageClass={this.props.headerImageClass}
          />
        ) : (
          <HeaderLeft
            title={title || 'Template Gallery.'}
            description={
              description ||
              '650+ pre-built form templates made by our team. Save time, get inspired, and make them your own. Search through hundreds of beautiful templates, and discover fresh new ways to use Paperform to simplify work.'
            }
            image={image || '/website/images/man-ipad.jpg'}
          />
        )}
        {this.props.pageContent}
        <div className={styles.filtersMobile}>
          <div className="max-width">{this.searchForm('mobile', query)}</div>
        </div>
        <div className={`max-width ${styles.pageWrapper}`}>
          <div className={styles.filters}>
            <div className={styles.filtersInner}>
              {this.searchForm('desktop', query)}
              <Link
                to="/templates/category/all/"
                key="all"
                className={styles.templateFilter}
              >
                All Templates
              </Link>
              {groupedForms.map((group) => (
                <Link
                  to={`/templates/category/${group.slug}/`}
                  key={group.slug}
                  className={styles.templateFilter}
                >
                  {group.title}
                </Link>
              ))}
            </div>
          </div>
          {this.getContent(query, groupedForms)}
        </div>
      </DefaultLayout>
    );
  }
}

export default function (props) {
  return (
    <StaticQuery
      query={query}
      render={(data) => (
        <Templates
          key={props.location && props.location.search}
          {...props}
          data={data}
        />
      )}
    />
  );
}
